import { syncAllIcloudCalendarsForDates } from 'components/providers/icloud/IcloudProvider'
import { OutlookContext } from 'components/providers/outlook/OutlookProvider'
import dayjs from 'dayjs'
import { uploadEventsBatch } from 'services/api/event'
import Database from 'services/db/Database'
import { Account } from 'services/db/Queries/AccountQueries'
import {
  allCalendarItemsForSync,
  localEventByConvertedLocalId
} from 'services/db/Queries/Event'
import {
  allGroups,
  thirdPartyProviderGroup
} from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AccountDoc,
  DocType,
  EventStatus,
  GroupDoc,
  TaskType
} from 'types/api/changes'
import { LocalStorageKey } from 'types/services/localStorage'
import { getOrUpdateDeviceId } from 'utils/device'
import { syncGoogleForDates } from 'utils/google_events'
import {
  groupToProvider,
  PersonalGroupSyncStatus,
  ProviderType
} from 'utils/groups'
import { isTrulyEmpty, removePrefix } from 'utils/object'
import { cacheConvertedCalendarsToLocalStorage } from 'utils/third_party_calendars'
import { isGuest } from 'utils/user'

import { highestActiveTier } from '../accounts'
import { TierFeatures } from '../group_state'
import { isFeatureAllowedToUser } from '../groups'

const BATCH_SIZE = 500 // Adjust batch size as needed
let isSyncLocalCalendarsToServerInProgress = false

export async function fetchAllFromThirdPartiesForSync(
  outlookContext?: OutlookContext
) {
  const fromDate = dayjs().subtract(1, 'month').toISOString()
  const toDate = dayjs().add(3, 'month').toISOString()
  Logger.orange(
    'CalendarSync:Syncing local calendars to server for dates:',
    fromDate,
    toDate,
    outlookContext
  )
  Logger.orange('CalendarSync:Sync complete for dates:', fromDate, toDate)

  const syncAll = [
    syncGoogleForDates(fromDate, toDate),
    syncAllIcloudCalendarsForDates(fromDate, toDate),
    outlookContext?.syncCalendars?.(fromDate, toDate)
  ]

  await Promise.all(syncAll)

  cacheConvertedCalendarsToLocalStorage()

  syncLocalCalendarsToServer()
}

export async function syncLocalCalendarsToServer(
  ignoreStatus: boolean = false
) {
  if (isSyncLocalCalendarsToServerInProgress) {
    return Promise.resolve()
  }

  isSyncLocalCalendarsToServerInProgress = true

  try {
    if (isGuest()) return

    const acc = await Account()

    markSyncProblemIfNeeded(acc)
    let allItemsToSync = await allCalendarItemsForSync()

    const groups: string[] = []

    for (const item of allItemsToSync) {
      if (item.GroupID && !groups.includes(item.GroupID)) {
        const groupInLocalDB = await thirdPartyProviderGroup(item.GroupID)

        if (
          groupInLocalDB?.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
        ) {
          Logger.red(
            'SyncLocalCalendarsToServer: Skipping item with GroupID that does not belong to user:',
            item
          )
          continue
        }

        if (groupInLocalDB.SyncProblem === '1') {
          continue
        }

        if (isTrulyEmpty(groupInLocalDB._rev)) {
          continue
        }

        if (
          !groupInLocalDB ||
          (!ignoreStatus &&
            (isTrulyEmpty(groupInLocalDB.PersonalGroupSyncStatus) ||
              groupInLocalDB.PersonalGroupSyncStatus !==
                PersonalGroupSyncStatus.SYNCED ||
              !isFeatureAllowedToUser(
                TierFeatures.SHARE_PERSONAL_CALENDAR,
                highestActiveTier(acc),
                groupInLocalDB,
                acc
              )))
        ) {
          continue
        }

        groups.push(item.GroupID)
      }
    }

    Logger.orange('CalendarSync:Groups to sync:', groups)
    // remmove items with groups that are not synced (not exist in groups array)
    allItemsToSync = allItemsToSync.filter((item) =>
      groups.includes(item.GroupID ?? '')
    )

    Logger.orange(
      `CalendarSync:Found ${allItemsToSync.length} calendar items for sync.`
    )

    if (allItemsToSync.length === 0) {
      Logger.orange('CalendarSync:No items to sync, exiting.')
      return
    }

    // Split into batches and upload
    for (let i = 0; i < allItemsToSync.length; i += BATCH_SIZE) {
      const batch = allItemsToSync.slice(i, i + BATCH_SIZE)
      Logger.orange(
        `CalendarSync:Uploading batch ${i / BATCH_SIZE + 1} (${batch.length} items)`
      )

      try {
        const responseArray = await uploadEventsBatch(
          batch.map((item) => {
            const mapped = Database.toServerObject(item)

            if (isTrulyEmpty(mapped._rev)) {
              delete mapped._rev
              delete mapped._id
            }

            delete mapped.Color
            delete mapped.canAddAttendees
            delete mapped.someday
            delete mapped.CustomUrl
            delete mapped.needSync

            mapped.Priority = '1'
            mapped.TaskType = TaskType.personalEvent
            mapped.isDeleted = '0'
            mapped.UserID = LocalStorage.get(LocalStorageKey.USER_ID)
            mapped.ObjectType = '1'
            mapped.DeviceChangeID = getOrUpdateDeviceId()
            mapped.Type = DocType.EVENT

            if (
              mapped.Location &&
              isTrulyEmpty(mapped.Location.FilteredLocation) &&
              typeof mapped.Location !== 'string'
            ) {
              mapped.Location.FilteredLocation = 'null'
            }

            if (mapped.Notes) {
              for (const note of mapped.Notes) {
                if (!note.FilePath && typeof note !== 'string') {
                  note.FilePath = 'null'
                }
              }
            }

            return mapped
          })
        )

        Logger.orange(
          `CalendarSync:Batch ${i / BATCH_SIZE + 1} response received.`
        )
        Logger.orange('CalendarSync:Batch response:', responseArray)

        for (const response of responseArray) {
          for (const batchItem of batch) {
            if (response.doc?.local_id === batchItem.local_id) {
              await Database.write(async () => {
                return batchItem.update((item) => {
                  item.needSync = '0'
                  item._rev = response.doc?._rev
                  item.Status = (String(response.doc?.Status) ??
                    item.Status) as EventStatus
                  item.LastUpdate =
                    String(response.doc?.LastUpdate) ?? item.LastUpdate

                  if (response.doc?._id) {
                    item._id = response.doc?._id
                  }
                })
              })
            }
          }
        }

        Logger.orange(
          `CalendarSync:Batch ${i / BATCH_SIZE + 1} uploaded successfully.`
        )
      } catch (error) {
        Logger.red('Error uploading batch:', error)
        // Optionally: break or continue depending on your error handling strategy
      }
    }
  } finally {
    isSyncLocalCalendarsToServerInProgress = false
  }
}

async function markSyncProblemIfNeeded(acc: AccountDoc | undefined) {
  if (!acc) return

  Logger.orange(`CalendarSync:ConnectedCalAccounts:`, acc.ConnectedCalAccounts)

  const groups = (await allGroups()).filter(
    (g) => g.CalendarEmail && g.CalendarType
  )
  for (const groupInLocalDB of groups) {
    if (
      isTrulyEmpty(groupInLocalDB._rev) ||
      groupInLocalDB.SyncProblem === '1'
    ) {
      continue
    }
    const provider = groupToProvider(groupInLocalDB)
    const calendarEmail = removePrefix(groupInLocalDB.CalendarEmail ?? '')
    let isEmailExist = acc.ConnectedCalAccounts[calendarEmail] !== undefined

    if (provider === ProviderType.GOOGLE) {
      isEmailExist =
        isEmailExist &&
        (acc.ConnectedCalAccounts[calendarEmail] as any).Google !== undefined
    } else if (provider === ProviderType.APPLE) {
      isEmailExist =
        isEmailExist &&
        (acc.ConnectedCalAccounts[calendarEmail] as any).iCloud !== undefined
    } else if (provider === ProviderType.OUTLOOK) {
      // outlookprovider handles this
      return false
    }

    const syncProblem =
      (Object.keys(acc.ConnectedCalAccounts ?? {}).length === 0 ||
        !isEmailExist) &&
      groupInLocalDB.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)

    if (syncProblem) {
      Logger.red(
        `CalendarSync:Group ${groupInLocalDB._id} is synced with server, but account does not have it in ConnectedCalAccounts.`
      )

      await Database.write(async () => {
        return groupInLocalDB.update((g) => {
          g.SyncProblem = '1'
        })
      })
    }
  }
}
